import axios from 'axios';
import { NextApiRequest, NextApiResponse } from 'next';

export interface LocationPrediction {
    description: string;
    matched_substrings: {
        length: string;
        offset: string;
    }[];
    place_id: string;
    reference: string;
    structured_formatting: {
        main_text: string;
        main_text_matched_substrings: {
            length: number;
            offset: number;
        }[];
        secondary_text: string;
    };
    terms: {
        offset: number;
        value: string;
    }[];
    types: ['locality', 'political', 'geocode'];
}

export interface PredictionsResponse {
    predictions: LocationPrediction[];
    status:
        | 'OK'
        | 'ZERO_RESULTS'
        | 'INVALID_REQUEST'
        | 'OVER_QUERY_LIMIT'
        | 'REQUEST_DENIED'
        | 'UNKNOWN_ERROR';
}

const allowedTypes = [
    '(cities)',
    '(regions)',
    'establishments',
    'address',
] as const;

type Type = (typeof allowedTypes)[number];

const googlePlacesBaseUrl =
    'https://maps.googleapis.com/maps/api/place/autocomplete/json';
const internalApiBaseUrl = '/api/getLocationAutocomplete';

export const getLocationSuggestions = (
    value: string,
    type: Type = '(cities)',
    countryCode?: string
) => {
    if (!value || typeof value !== 'string') {
        throw new Error('Invalid input value');
    }

    const sanitizedValue = value
        .replace(/[<>]/g, '')
        .replace(/[&|;]/g, '')
        .replace(/[^\w\s\-.,]/g, '')
        .trim();

    if (sanitizedValue.length === 0) {
        throw new Error('Input value cannot be empty after sanitization');
    }

    if (sanitizedValue.length > 100) {
        throw new Error('Input value is too long');
    }

    if (!allowedTypes.includes(type)) {
        throw new Error('Invalid type');
    }

    if (countryCode) {
        if (!/^[A-Z]{2}$/.test(countryCode)) {
            throw new Error('Invalid country code');
        }
    }

    const params = new URLSearchParams();

    params.append('input', sanitizedValue);
    params.append('type', type);
    if (countryCode) {
        params.append('countryCode', countryCode);
    }

    const safeUrl = `${internalApiBaseUrl}?${params.toString()}`;

    return axios.get<PredictionsResponse>(safeUrl, {
        maxRedirects: 0,
        timeout: 5000,
        validateStatus: (status) => status >= 200 && status < 300,
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
        },
    });
};

const handler = async (req: NextApiRequest, res: NextApiResponse) => {
    const apiKey = process.env.NEXT_PUBLIC_GOOGLE_LOCATIONS_KEY;

    if (!apiKey) {
        console.error('Google Places API key is missing on the server.');
        return res
            .status(500)
            .json({ error: 'Internal server configuration error.' });
    }

    const { input, type, countryCode } = req.query;

    if (
        !input ||
        typeof input !== 'string' ||
        input.trim().length === 0 ||
        input.length > 100
    ) {
        return res.status(400).json({
            error: 'Invalid or missing input parameter (max 100 chars).',
        });
    }
    const sanitizedInput = (input as string).trim();

    if (
        !type ||
        typeof type !== 'string' ||
        !allowedTypes.includes(type as Type)
    ) {
        return res
            .status(400)
            .json({ error: 'Invalid or missing type parameter.' });
    }

    if (countryCode) {
        if (
            typeof countryCode !== 'string' ||
            !/^[A-Z]{2}$/.test(countryCode)
        ) {
            return res.status(400).json({
                error: 'Invalid country code parameter (must be 2 uppercase letters).',
            });
        }
    }

    const url = new URL(googlePlacesBaseUrl);
    url.searchParams.append('input', sanitizedInput);
    url.searchParams.append('types', type);
    url.searchParams.append('key', apiKey);
    if (countryCode) {
        url.searchParams.append('components', `country:${countryCode}`);
    }
    const stringUrl = url.toString();

    try {
        const { data } = await axios.get<PredictionsResponse>(stringUrl, {
            maxRedirects: 0,
            timeout: 5000,
            validateStatus: (status) => status >= 200 && status < 300,
            headers: { Accept: 'application/json' },
        });
        res.status(200).json(data);
    } catch (error) {
        console.error('Error fetching data from Google Places API:', error);
        res.status(500).json({
            error: 'Error fetching data from Google Places API',
        });
    }
};

export default handler;
